import styles from "./passedAndFailedResultFilter.module.scss";
import {
  ResultEnum,
  TransformExecuteResponse
} from "../../types/clientTypes";
import { useTranslation } from "react-i18next";
import {
  useEffect,
  useMemo,
  useState
} from "react";
import {
  useForm,
  useWatch
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  object,
  string
} from "yup";
import { Dropdown } from "../../shared/components/dropdown/dropdown";
import { ReactComponent as ArrowSvg } from "../../shared/images/arrow.svg";
import { ReactComponent as SuccessSvg } from "../../shared/images/success.svg";
import { ReactComponent as CloseSvg } from "../../shared/images/close.svg";
import { ReactComponent as SkipSvg } from "../../shared/images/skip.svg";

export type PassedAndFailedResultProps = {
  onChangeFilter: (filterResult?: ResultEnum) => void;
  filter?: ResultEnum
  executeResponses: Array<TransformExecuteResponse>;
}

export const PassedAndFailedResultFilter = ({ executeResponses, filter, onChangeFilter }: PassedAndFailedResultProps) => {
  const { t } = useTranslation();

  const [isOpenDropdown, setIsOpenDropdown] = useState<boolean>(false);

  const [passedTestCasesLength, setPassedTestCasesLength] = useState<number>(0);
  const [passedExecutionTimeLength, setPassedExecutionTimeLength] = useState<number>(0);
  const [failedTestCasesLength, setFailedTestCasesLength] = useState<number>(0);
  const [skippedTestCasesLength, setSkippedTestCasesLength] = useState<number>(0);

  const schema = object({
    result: string().required(),
  });

  const { control, setValue } = useForm({
    resolver: yupResolver(schema),
  });
  const resultWatch = useWatch({ control, name: "result" });

  useEffect(() => {
    let newPassedTestCasesLength = 0;
    let newPassedExecutionTimeLength = 0;
    let newFailedTestCasesLength = 0;
    let newSkippedTestCasesLength = 0;
    executeResponses.forEach(executeResponse => {
      if (!!executeResponse) {
        switch (executeResponse.result) {
          case ResultEnum.passed:
            newPassedTestCasesLength++;
            break;
          case ResultEnum.passedExecutionTime:
            newPassedExecutionTimeLength++;
            break;
          case ResultEnum.skipped:
            newSkippedTestCasesLength++
            break;
          case ResultEnum.failed:
            newFailedTestCasesLength++
            break;
        }
      }
    });
    setPassedTestCasesLength(newPassedTestCasesLength);
    setPassedExecutionTimeLength(newPassedExecutionTimeLength);
    setFailedTestCasesLength(newFailedTestCasesLength);
    setSkippedTestCasesLength(newSkippedTestCasesLength);
  }, [setPassedTestCasesLength, setPassedExecutionTimeLength, setFailedTestCasesLength, setSkippedTestCasesLength, executeResponses]);

  useEffect(() => {
    setValue("result", filter || "all");
  }, [setValue, filter]);

  useEffect(() => {
    switch (resultWatch) {
      case ResultEnum.passed:
        onChangeFilter(ResultEnum.passed);
        break;
      case ResultEnum.passedExecutionTime:
        onChangeFilter(ResultEnum.passedExecutionTime);
        break;
      case ResultEnum.skipped:
        onChangeFilter(ResultEnum.skipped);
        break;
      case ResultEnum.failed:
        onChangeFilter(ResultEnum.failed);
        break;
      default:
        onChangeFilter();
        break;
    }
  }, [resultWatch, onChangeFilter]);

  const dropdownItems = useMemo(() => {
    return [{
      id: "all",
      name: t("common.all"),
      title: t("common.all")
    }, {
      id: ResultEnum.passed,
      name: t("common.passed"),
      title: t("common.passed")
    }, {
      id: ResultEnum.passedExecutionTime,
      name: t("common.timeExceeded"),
      title: t("common.timeExceeded")
    }, {
      id: ResultEnum.failed,
      name: t("common.failed"),
      title: t("common.failed")
    }, {
      id: ResultEnum.skipped,
      name: t("common.skipped"),
      title: t("common.skipped")
    }];
  }, []);

  const passedComponent = useMemo(() => {
    return (
      <div className={styles.item}>
        <SuccessSvg />
        {passedTestCasesLength}
      </div>
    )
  }, [passedTestCasesLength]);

  const exceededComponent = useMemo(() => {
    return (
      <div className={styles.item}>
        <SuccessSvg className={styles.exceeded}/>
        {passedExecutionTimeLength}
      </div>
    )
  }, [passedExecutionTimeLength]);

  const failedComponent = useMemo(() => {
    return (
      <div className={styles.item}>
        <CloseSvg className={styles.failed} />
        {failedTestCasesLength}
      </div>
    )
  }, [failedTestCasesLength]);

  const skipComponent = useMemo(() => {
    return (
      <div className={styles.item}>
        <SkipSvg />
        {skippedTestCasesLength}
      </div>
    )
  }, [skippedTestCasesLength]);

  const passedAndFailedResult = useMemo(() => {
    switch (resultWatch) {
      case ResultEnum.passed:
        return passedComponent;
      case ResultEnum.passedExecutionTime:
        return exceededComponent;
      case ResultEnum.failed:
        return failedComponent;
      case ResultEnum.skipped: {
        return skipComponent
      }
      default:
        return (<>
          {passedComponent}
          {exceededComponent}
          {failedComponent}
          {skipComponent}
        </>)
    }
  }, [passedComponent, exceededComponent, failedComponent, skipComponent, resultWatch]);

  const arrowClass = useMemo(() => {
    let result = styles.arrow;
    result += isOpenDropdown ? ` ${styles.open}` : "";
    return result;
  }, [isOpenDropdown]);

  return (
      <>
        <div className={styles.container} onClick={(e) => e.stopPropagation()}>
          <div className={styles.dropdown}>
            <Dropdown items={dropdownItems}
                      fontPopupClass={styles.fontPopup}
                      isOpenProp={isOpenDropdown}
                      setIsOpenProp={setIsOpenDropdown}
                      name={"result"}
                      control={control}/>
          </div>
          <div className={styles.items}>
            {passedAndFailedResult}
          </div>
          <div className={styles.arrowBlock}>
            <ArrowSvg className={arrowClass}/>
          </div>
        </div>
      </>

  );
}